import { cn, cva, NoddiIcon, type VariantProps } from 'noddi-ui-common';

const groupVariants = cva('flex items-center overflow-hidden rounded-[10px] border border-border', {
  variants: {
    size: {
      medium: '',
      large: ''
    }
  },
  defaultVariants: { size: 'medium' }
});

const buttonVariants = cva(
  'flex items-center justify-center text-foreground transition-colors hover:bg-card-light-background disabled:cursor-not-allowed disabled:opacity-40',
  {
    variants: {
      size: {
        medium: 'size-10',
        large: 'size-13'
      }
    },
    defaultVariants: { size: 'medium' }
  }
);

const readoutVariants = cva('flex items-center justify-center font-semibold tabular-nums', {
  variants: {
    size: {
      medium: 'h-10 min-w-14 px-2 text-base',
      large: 'h-13 min-w-16 px-2 text-lg'
    }
  },
  defaultVariants: { size: 'medium' }
});

export type NoddiNumberStepperProps = VariantProps<typeof groupVariants> & {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  /** How much one click moves the value. @default 1 */
  step?: number;
  /** Renders the value, e.g. `(minutes) => `${minutes} min``. Defaults to the bare number. */
  formatValue?: (value: number) => string;
  /** Accessible names for the two buttons — say what the click does, not "minus" / "plus". */
  decrementLabel: string;
  incrementLabel: string;
  disabled?: boolean;
  className?: string;
};

/**
 * The one −/+ control for a bounded number. Clicks move by `step` and clamp to the range, so a
 * caller never has to re-implement the arithmetic or the disabled edges.
 */
export const NoddiNumberStepper = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  formatValue,
  decrementLabel,
  incrementLabel,
  disabled,
  size,
  className
}: NoddiNumberStepperProps) => {
  const clamp = (next: number) => Math.min(max, Math.max(min, next));

  return (
    <div className={cn(groupVariants({ size }), className)}>
      <button
        type='button'
        aria-label={decrementLabel}
        disabled={disabled || value <= min}
        onClick={() => onChange(clamp(value - step))}
        className={cn(buttonVariants({ size }), 'border-r border-border')}
      >
        <NoddiIcon name='Minus' size='small' />
      </button>
      <span className={readoutVariants({ size })}>{formatValue ? formatValue(value) : value}</span>
      <button
        type='button'
        aria-label={incrementLabel}
        disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + step))}
        className={cn(buttonVariants({ size }), 'border-l border-border')}
      >
        <NoddiIcon name='Plus' size='small' />
      </button>
    </div>
  );
};
